import {
  IHazardStatements,
  IPrecautionaryStatement,
  IProduct,
} from '@core/api/Products/types'
import { i18n } from '@core/i18n/i18n'

/**
 * String used to indicate placeholders in translations of GHS statements
 */
const GHS_PHRASE_PLACEHOLDER = '…'

const hasPlaceholders = (phrase: string) =>
  phrase.indexOf(GHS_PHRASE_PLACEHOLDER) >= 0

/**
 * GHS statements can contain placeholders that a manufacturer of a product is supposed to fill out with specifics
 * Currently we don't have product specific statements in the API and can only show generic statements with placeholders
 * As a workaround we add a notice that the customer is supposed to look at the safety data sheet attachments to get details
 * and assume a client will provide these safety data sheets as part of the attachments
 * @param phrase
 */
const sanitize = (phrase: string) => {
  if (!hasPlaceholders(phrase)) return phrase.split('/').join(' / ')

  return `${phrase} ${i18n.t('ghsSystem.dataSheetNotice')}`
}

export const getHazardStatementPhrase = (
  hazardStatement: IHazardStatements
) => {
  return sanitize(hazardStatement.phrase)
}

export const getPrecautionaryStatementPhrase = (
  precautionaryStatement: IPrecautionaryStatement
) => {
  return sanitize(precautionaryStatement.phrase)
}

/**
 * Depending on the types of GHS classes and hazard statements,
 * we are required to display a specific / dynamic signal word above the statements
 * Current default implementation is a quickfix for Igefa / Idealclean that looks up the key in the product attributes
 * See: https://lyskahq.atlassian.net/browse/CF-2143?focusedCommentId=38656 & https://lyskahq.atlassian.net/browse/LCP-1271
 * @param product
 */
export const getDangerSignalWord = (product: IProduct) => {
  const signalWordAttribute = product.clientFields.attributes?.find(
    (attribute) => attribute.key === '9901'
  )

  return (
    signalWordAttribute?.value ||
    i18n.t('productDetail.hazardWarnings.defaultDangerSignalWord')
  )
}
